import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import InlineError from '../InlineError';
import {
  CrashDescriptionMaxLength,
  CrashReport,
  crashHeadline,
  crashReportStack,
  sendCrashReport,
} from '../../services/crashReport';
import { chatFailureMessage } from '../../utils/actionFailure';

interface CrashReportDialogProps {
  projectId: string;
  report: CrashReport;
  version: string | null;
  canRequestFix: boolean;
  onClose: () => void;
}

const panelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'calc(100dvh - 48px)',
  outline: 'none',
};

const scrollingBodyStyle: CSSProperties = { flex: '1 1 auto', minHeight: 0, overflowY: 'auto' };

export default function CrashReportDialog({ projectId, report, version, canRequestFix, onClose }: CrashReportDialogProps) {
  const navigate = useNavigate();
  const panel = useRef<HTMLDivElement>(null);
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const close = useCallback(() => {
    if (!sending) onClose();
  }, [sending, onClose]);

  useEffect(() => {
    panel.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const send = async () => {
    setSending(true);
    setFailure(null);
    try {
      await sendCrashReport(projectId, report, version, description);
      navigate(`/projects/${projectId}`);
    } catch (error) {
      setFailure(chatFailureMessage(error, serverMessageOf(error) ?? 'The fix request was not sent. Try again.'));
      setSending(false);
    }
  };

  if (!canRequestFix) {
    return (
      <div className="modal-backdrop">
        <div
          ref={panel}
          className="modal-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="crash-report-title"
          tabIndex={-1}
          style={panelStyle}
        >
          <div style={scrollingBodyStyle}>
            <h3 id="crash-report-title" className="display tight" style={{ fontSize: 24, margin: '0 0 10px' }}>
              Sorry, something went wrong
            </h3>
            <p className="muted-2" style={{ fontSize: 13.5, lineHeight: 1.55, margin: 0 }}>
              The game stopped unexpectedly. Close this message to start it again.
            </p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 22, flex: 'none' }}>
            <button type="button" className="btn primary" onClick={close}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop">
      <div
        ref={panel}
        className="modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="crash-report-title"
        tabIndex={-1}
        style={{ ...panelStyle, maxWidth: 560 }}
      >
        <div style={scrollingBodyStyle}>
          <div className="eyebrow" style={{ color: 'var(--danger)', marginBottom: 8 }}>
            Error
          </div>
          <h3 id="crash-report-title" className="display tight" style={{ fontSize: 24, margin: '0 0 16px' }}>
            The game crashed
          </h3>

          <CrashData report={report} />

          <textarea
            className="textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={CrashDescriptionMaxLength}
            disabled={sending}
            placeholder="What were you doing when it crashed, or how can it be made to happen again? (optional)"
            aria-label="What happened"
          />
        </div>

        <div style={{ flex: 'none' }}>
          {failure && (
            <div style={{ marginTop: 14 }}>
              <InlineError>{failure}</InlineError>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 22 }}>
            <button type="button" className="btn" onClick={close} disabled={sending}>
              Close
            </button>
            <button type="button" className="btn primary" onClick={send} disabled={sending}>
              {sending ? 'Sending…' : 'Send a fix request'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CrashData({ report }: { report: CrashReport }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
      <div className="mono" style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-5)' }}>
        Sent with the fix request
      </div>
      {report.screenshot && (
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 8,
            overflow: 'hidden',
            border: '1px solid var(--line-2)',
            flex: 'none',
          }}
        >
          <img
            src={report.screenshot}
            alt="The game when it crashed"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      )}
      <div
        className="mono"
        style={{ fontSize: 12.5, lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', color: 'var(--text)' }}
      >
        {crashHeadline(report.error)}
      </div>
      <pre
        aria-label="Stack"
        className="mono scroll-thin"
        style={{
          margin: 0,
          maxHeight: 120,
          overflow: 'auto',
          padding: '8px 10px',
          fontSize: 11.5,
          lineHeight: 1.45,
          color: 'var(--text-4)',
          background: 'var(--surface)',
          border: '1px solid var(--line-2)',
          borderRadius: 8,
        }}
      >
        {crashReportStack(report.error)}
      </pre>
    </div>
  );
}

function serverMessageOf(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const message = (error.response?.data as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' && message ? message : null;
}
